import { Menu, Popover, Transition } from "@headlessui/react"
import { ArrowLeftIcon, ChevronDownIcon, EllipsisHorizontalIcon, KeyIcon, MoonIcon, PencilIcon, SunIcon, TrashIcon, ShareIcon, ArrowDownTrayIcon, EyeIcon } from "@heroicons/react/24/outline"
import { Row } from "@tanstack/react-table"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { NavigationLink } from "../../components/navigationLink"
import { RouteElement } from "../../interfaces/_common"
import { Fragment, SVGProps, useState } from "react"

import { Img } from "../../components/image"
import { CheckCircleIcon, MinusCircleIcon, UserCircleIcon } from "@heroicons/react/24/solid"
import { Button } from "../../components/ui/button"
import { useAuth } from "../../context/authContext"
import { SmoothTransition } from "../../components/pageTransition"
import ChangePasswordForm from "../ChangePassword/ChangePasswordForm"
import { DialogWrapper } from "../../../src/components/dialogWrapper";
import { hasAccess } from "constants/permission.utils"


export const RenderDefaultTemplate = (
    row: any,
    accessorKey: string = 'is_default',
    onChange: (rowData: any) => void
) => {
    return (
        <div className="px-1 flex">
            <div>
                <label
                    title={row.original?.[accessorKey] ? 'Default' : 'Not Default'}
                    htmlFor={`${accessorKey}-toggle` + row.id}
                    className="inline-flex relative items-center mr-5 cursor-pointer"
                >
                    <input
                        id={`${accessorKey}-toggle` + row.id}
                        type="checkbox"
                        className="sr-only peer"
                        checked={Boolean(row.original?.[accessorKey])}
                        onChange={() => onChange(row.original)}
                    />

                    <div className="
            w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-600 peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600
          " />
                </label>
            </div>
        </div>
    )
}



export const RenderStatus = (row: Row<any>, accessorKey: string = 'is_premium', onChange: (rowData: any) => void) => {
    return (
        <div className="px-1 flex">
            <div>
                <label title={row.original?.[accessorKey] ? 'Active' : 'Inactive'} htmlFor={`${accessorKey}-toggle` + row.id} className="inline-flex relative items-center mr-5 cursor-pointer">
                    <input onChange={() => onChange(row.original)} id={`${accessorKey}-toggle` + row.id} type="checkbox" value={1} checked={row.original?.[accessorKey]} className="sr-only peer" />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-600 peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
            </div>
        </div>
    )
}

export const RenderIsPublished = (row: Row<any>, accessorKey: string = 'is_premium', onChange: (rowData: any) => void) => {
    return (
        <div className="px-1 flex">
            <div>
                <label title={row.original?.[accessorKey] ? 'Publish' : 'Draft'} htmlFor={`${accessorKey}-toggle` + row.id} className="inline-flex relative items-center mr-5 cursor-pointer">
                    <input onChange={() => onChange(row.original)} id={`${accessorKey}-toggle` + row.id} type="checkbox" value={1} checked={row.original?.[accessorKey]} className="sr-only peer" />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-600 peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
            </div>
        </div>
    )
}


export type defaultActions = 'edit' | 'view' | 'delete' | 'share' | 'download'
export const RenderDefaultActions = (row: Row<any>, actions: defaultActions[], onAction: (type: string, rowData: any) => void) => {
    return (
        <div className="px-1 flex">
            {
                actions.includes('view')
                &&
                <button title="View" className='px-1 py-1 text-lg hover:opacity-60' onClick={() => { onAction('view', row.original) }} >
                    <EyeIcon className='h-4 w-4 text-blue-800 dark:text-blue-500' />
                </button>
            }

            {
                actions.includes('edit')
                &&
                <button title="Edit" className='px-1 py-1 text-lg hover:opacity-60' onClick={() => { onAction('edit', row.original) }} >
                    <PencilIcon className='h-4 w-4 text-blue-800 dark:text-blue-500' />
                </button>
            }

            {
                actions.includes('delete')
                &&
                <button title="Delete" className='px-1 py-1 text-red-600 text-lg hover:opacity-60' onClick={() => { onAction('delete', row.original) }} >
                    <TrashIcon className='h-4 w-4 ' />
                </button>
            }

            {
                actions.includes('share')
                &&
                <button className='px-1 py-1 text-blue-600 text-lg hover:opacity-60 ml-2' onClick={() => { onAction('share', row.original) }} >
                    <ShareIcon className='h-4 w-4 ' />
                </button>
            }

            {
                actions.includes('download')
                &&
                <button className='px-1 py-1 text-green-600 text-lg hover:opacity-60 ml-2' onClick={() => { onAction('download', row.original) }} >
                    <ArrowDownTrayIcon className='h-4 w-4 ' />
                </button>
            }
        </div>
    )
}



interface MultiAction {
    type: string,
    Icon?: any
}
export const RenderMultiActionMenu = (row: Row<any>, actions: MultiAction[], onAction: (type: string, rowData: any) => void) => {
    return <>
        <Menu as="div" className="relative inline-flex text-left items-center">
            <Menu.Button >
                <EllipsisHorizontalIcon
                    className="ml-2 mr-1 h-5 w-5  hover:text-blue-800"
                    aria-hidden="true"
                />
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="z-10 absolute top-0 right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-slate-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="px-1 py-1 ">
                        {
                            actions?.map((option, index) => (
                                <Menu.Item key={`menuItem${index}`}>
                                    {({ active }) => (
                                        <button
                                            onClick={() => onAction(option.type, row.original)}
                                            className={`${active ? 'bg-blue-500 text-white dark:bg-slate-700 ' : 'text-gray-900 dark:text-gray-200'
                                                } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                        >
                                            {
                                                option.Icon &&
                                                <option.Icon
                                                    className="mr-2 h-5 w-5"
                                                    aria-hidden="true"
                                                />
                                            }
                                            {option.type}
                                        </button>
                                    )}
                                </Menu.Item>
                            ))
                        }
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    </>
}
interface SubMenuTabsProps {
    _routes: RouteElement[]
}
export const SubMenuTabs: React.FC<SubMenuTabsProps> = ({ _routes }) => {
    let pathname = useLocation().pathname
    const auth = useAuth()
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false);

    let activeRoute = _routes.filter(route => (route.end ? pathname === route.route : pathname?.includes(`${route.route}`)))?.[0]
    return (
        <div className="w-full  px-0 md:px-3 bg-white dark:bg-slate-800 border-0 flex items-center">
            <div className="border-b border-gray-200 dark:border-slate-900 w-full flex md:block items-center justify-between">

                <div className="w-full md:overflow-x-auto w-full md:w-[calc(100%-10%)] float-left">

                        <ul className="flex text-sm font-medium text-center text-gray-500 dark:text-gray-400 tracking-[-0.2px]">
                            <li className="visible md:hidden inline-flex items-center gap-2 relative ml-auto">
                                {hasAccess(activeRoute?.key, auth?.user?.permissions) && (<Menu>
                                    <Menu.Button className={"p-4 rounded-t-lg active text-blue-600 dark:text-blue-400  inline-flex gap-2"}>
                                        {activeRoute?.icon && <activeRoute.icon className="w-5 h-5" />}
                                        {activeRoute?.title}
                                        <ChevronDownIcon className="w-4 h-4 mt-0.5" />
                                    </Menu.Button>
                                    <Menu.Items className="absolute top-0 right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-slate-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                                        <div className="px-1 py-1 ">
                                            {
                                                _routes?.filter(route => route.display)?.map((route, index) => (
                                                    hasAccess(route.key, auth?.user?.permissions) && (<Menu.Item key={index}>
                                                        {({ active }) => (
                                                            <NavigationLink to={route.route} onMatchClass="active text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400" key={'mediaListI' + route.route}
                                                                className={`p-4 rounded-t-lg border-b-2 dark:border-slate-800 flex`}>
                                                                {route.icon && <route.icon className="mr-2 w-5 h-5  group-hover:text-gray-500 " />}
                                                                {route.title}
                                                            </NavigationLink>
                                                        )}
                                                    </Menu.Item>)
                                                ))
                                            }
                                        </div>
                                    </Menu.Items>
                                </Menu>)}
                            </li>
                            {_routes.map(route => route.display && hasAccess(route?.key, auth?.user?.permissions) && (
                                <li key={'route-' + route.route} className="hidden md:inline-flex">
                                    <NavigationLink end={route.end} to={route.route} onMatchClass="active text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400 inline-flex"
                                        className={`px-2 py-4 rounded-t-lg border-b-2 dark:border-slate-800 flex whitespace-nowrap`}>
                                        {route.icon && <route.icon className="mr-1 w-5 h-5  group-hover:text-gray-500 " />}
                                        {route.title}
                                    </NavigationLink>
                                </li>
                            ))}

                        </ul>

                </div>

                <ul className="float-right py-3">
                    <li className="inline-flex items-center gap-2 relative md:ml-auto">
                        <Popover>
                            <Popover.Button className={'hover:brightness-90 outline-none mr-2'}>
                                <span className="h-8 w-8 bg-blue-500 text-white rounded-full text-xs flex items-center justify-center font-semibold">{auth?.user?.username?.split(" ")?.map(u => u[0]?.toUpperCase())?.join("")}</span>
                            </Popover.Button>
                            <Popover.Panel as="div" className={'z-50 absolute top-16 right-0'}>
                                <SmoothTransition className=" flex flex-col items-center rounded-md bg-white dark:bg-slate-900 dark:border-slate-800 p-3 shadow-xl shadow-slate-400 dark:shadow-gray-800 border min-w-[200px] ">
                                    <span className="h-20 w-20 bg-blue-500 text-white rounded-full text-lg flex items-center justify-center font-semibold">{auth?.user?.username?.split(" ")?.map(u => u[0]?.toUpperCase())?.join("")}</span>
                                    <h1 className="text-lg font-semibold text-black my-2 dark:text-white">{auth?.user?.username}</h1>
                                    <span className="text-xs font-semibold flex gap-2 items-center mb-3">{auth?.user?.role} <CheckCircleIcon className="h-4 w-4 text-blue-600" /> </span>
                                    <Button className="w-full text-xs" onClick={() => auth?.setDarkTheme(!!!auth?.darkTheme)} color={auth?.darkTheme ? 'blue' : 'basic'}>
                                        Dark Theme <MoonIcon className="h-3 w-3" />
                                    </Button>
                                    <Button onClick={() => setIsOpen(true)} className="w-full text-xs" color="basic">Change Password <KeyIcon className="h-3 w-3" /> </Button>
                                    <Button onClick={() => auth?.logout()} className="w-full text-xs" color="red">Signout <ArrowLeftIcon className="h-4 w-4" /> </Button>
                                </SmoothTransition>
                            </Popover.Panel>
                        </Popover>
                    </li>

                    {isOpen && (
                        <DialogWrapper
                            isOpen={isOpen}
                            setIsOpen={setIsOpen}
                            title="Change Password"
                            panelClass="max-w-md"
                        >
                            <ChangePasswordForm />
                        </DialogWrapper>
                    )}
                </ul>

            </div>
        </div>

    )


}

interface RenderWizardProps {
    row: Row<any> | any, accessorKey: string, height?: string, width?: string, className?: string
}
export const RenderImageWizard: React.FC<RenderWizardProps> = ({ row, accessorKey = 'image', height, width, className }) => {

    return (
        <div className="flex">
            <div className={row.original[accessorKey] ? `cursor-pointer` : ''}>
                <Img src={row.original[accessorKey]} {...{ height, width, className }} />
            </div>
        </div>
    )
}


export const ColoredBg = (color: any) => {
    return <svg className="h-full" viewBox="0 0 119 70" preserveAspectRatio="none" ><defs><filter id="kx89ggfbla" width="100%" height="100%" filterUnits="objectBoundingBox"><feOffset dy="3" in="SourceAlpha" result="shadowOffsetOuter1"></feOffset><feGaussianBlur in="shadowOffsetOuter1" result="shadowBlurOuter1" stdDeviation="1.5"></feGaussianBlur><feColorMatrix in="shadowBlurOuter1" values="0 0 0 0 0.75866284 0 0 0 0 0.75866284 0 0 0 0 0.75866284 0 0 0 0.330615942 0"></feColorMatrix></filter><path id="nt3vuqn7wb" d="M115.87 0c1.641 0 2.972 1.33 2.972 2.971v43.664c0 1.403-.981 2.614-2.353 2.906-2.125.453-4.255.879-6.388 1.28l.026-.11c-1.664 7.244-6.378 11.47-14.143 12.678 2.722-1.975 4.887-7.99 1.033-10.444-12.24 1.68-24.606 2.52-37.101 2.52-19.179 0-38.36-1.979-57.545-5.936C.99 49.244 0 48.029 0 46.619V2.971C0 1.331 1.33 0 2.971 0h112.9z"></path></defs><g fill="none" fillRule="evenodd"><g><g><use fill={color} xlinkHref="#nt3vuqn7wb"></use></g></g></g></svg>
}